import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useApi, errMsg, DAY_NAMES } from "@/lib/api";
import { useInfo } from "@/context/InfoContext";
import { inputCls, primaryBtn } from "@/pages/admin/AdminServices";

const SOCIAL = [["instagram", "Instagram"], ["tiktok", "TikTok"], ["facebook", "Facebook"], ["youtube", "YouTube"]];

export default function AdminSettings() {
  const api = useApi();
  const { info, reload } = useInfo();
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (info && !f) {
      setF(JSON.parse(JSON.stringify(info.settings)));
    }
  }, [info, f]);

  if (!f) return <Loader2 className="w-5 h-5 animate-spin text-zinc-400" />;

  const setHour = (day, patch) => setF({ ...f, hours: f.hours.map((h) => (h.day === day ? { ...h, ...patch } : h)) });

  const save = async (e) => {
    e.preventDefault();
    for (const k of ["instagram", "tiktok", "facebook", "youtube"]) {
      if (f[k] && !f[k].startsWith("https://")) return toast.error(`El enlace de ${k} debe empezar por https://`);
    }
    setBusy(true);
    try {
      const { id, ...rest } = f;
      await api("put", "/admin/settings", { ...rest, slot_step: Number(f.slot_step), cancel_hours: Number(f.cancel_hours) });
      toast.success("Ajustes guardados");
      reload();
    } catch (x) { toast.error(errMsg(x)); } finally { setBusy(false); }
  };

  return (
    <form onSubmit={save} className="grid grid-cols-1 lg:grid-cols-2 gap-10" data-testid="admin-settings">
      <div className="space-y-6">
        <div>
          <p className="font-semibold mb-3">Horario del negocio</p>
          <ul className="divide-y divide-zinc-200 border-y border-zinc-200">
            {f.hours.map((h) => (
              <li key={h.day} className="py-3 flex flex-wrap items-center gap-3" data-testid={`admin-hours-${h.day}`}>
                <span className="w-24 text-sm">{DAY_NAMES[h.day]}</span>
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" className="accent-[#111111]" checked={!h.closed} disabled={h.day === 6} onChange={(e) => setHour(h.day, { closed: !e.target.checked })} data-testid={`admin-hours-open-${h.day}`} /> Abierto</label>
                {!h.closed && (
                  <>
                    <input type="time" step="900" className={inputCls} value={h.open} onChange={(e) => setHour(h.day, { open: e.target.value })} data-testid={`admin-hours-from-${h.day}`} />
                    <input type="time" step="900" className={inputCls} value={h.close} onChange={(e) => setHour(h.day, { close: e.target.value })} data-testid={`admin-hours-to-${h.day}`} />
                  </>
                )}
                {h.day === 6 && <span className="text-xs text-zinc-400">Siempre cerrado</span>}
              </li>
            ))}
          </ul>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <label className="text-xs text-zinc-500">Intervalo entre horas
            <select className={`${inputCls} w-full mt-1`} value={f.slot_step} onChange={(e) => setF({ ...f, slot_step: Number(e.target.value) })} data-testid="admin-slot-step">
              {[15, 30, 45, 60].map((m) => <option key={m} value={m}>{m} min</option>)}
            </select>
          </label>
          <label className="text-xs text-zinc-500">Cancelación online hasta (horas antes)
            <input type="number" min="0" max="168" className={`${inputCls} w-full mt-1`} value={f.cancel_hours} onChange={(e) => setF({ ...f, cancel_hours: e.target.value })} data-testid="admin-cancel-hours" />
          </label>
        </div>
      </div>
      <div className="space-y-4">
        <p className="font-semibold">Contacto y redes sociales</p>
        <p className="text-xs text-zinc-500">Solo se muestran en la web los campos que rellenes.</p>
        <label className="block text-xs text-zinc-500">WhatsApp (con prefijo, p. ej. 34XXXXXXXXX)<input className={`${inputCls} w-full mt-1`} value={f.whatsapp} onChange={(e) => setF({ ...f, whatsapp: e.target.value })} data-testid="admin-whatsapp" /></label>
        <label className="block text-xs text-zinc-500">Teléfono<input className={`${inputCls} w-full mt-1`} value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} data-testid="admin-phone" /></label>
        <label className="block text-xs text-zinc-500">Correo de contacto<input type="email" className={`${inputCls} w-full mt-1`} value={f.contact_email} onChange={(e) => setF({ ...f, contact_email: e.target.value })} data-testid="admin-contact-email" /></label>
        {SOCIAL.map(([k, l]) => (
          <label key={k} className="block text-xs text-zinc-500">{l}<input className={`${inputCls} w-full mt-1`} placeholder="https://…" value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`admin-social-${k}`} /></label>
        ))}
        <button disabled={busy} className={primaryBtn} data-testid="admin-settings-save">{busy && <Loader2 className="w-4 h-4 animate-spin" />} Guardar ajustes</button>
      </div>
    </form>
  );
}
